import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateRecordDetailModal, {
  PlateRecordDetailModalProps,
} from 'samples/plate-maps/plate-record/PlateRecordDetailModal';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ModalAnchor from 'ui-library/ModalAnchor';

export interface PlateMapViewDetailsButtonProps {
  plateRecordDetailModalProps: PlateRecordDetailModalProps;
  analyticsMetadata: AnalyticsEventMetadata;
}

/**
 * Render a button (and ModalAnchor) for open the details modal for a plate record/plate design.
 * @param plateRecordDetailModalProps Props to thread through to the modal
 * @param plateMapType Type of the plate map
 * @param analyticsMetadata Metadata to include in the open modal event fired when the button is clicked.
 * @constructor
 */
export default function PlateMapViewDetailsButton({
  plateRecordDetailModalProps,
  analyticsMetadata,
}: PlateMapViewDetailsButtonProps): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);

  const runPlateMapViewDetailsModal = (): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.SAMPLES, {
      ...analyticsMetadata,
      modalType: FixedPlateModalType.PLATE_MAP_VIEW_DETAILS_MODAL,
    });
    setModalOpen(true);
  };

  // Use a modal anchor to ensure the modal is closed when a user clicks on a chip within the detail modal.
  return (
    <>
      <ModalAnchor open={modalOpen}>
        <PlateRecordDetailModal
          {...plateRecordDetailModalProps}
          onRequestClose={() => {
            plateRecordDetailModalProps.onRequestClose();
            setModalOpen(false);
          }}
        />
      </ModalAnchor>
      <Button
        className={classNames(css(styles.button), 'hide-on-print')}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.SMALL}
        text="View details"
        onClick={runPlateMapViewDetailsModal}
        disabled={false}
        dataTestAttrs={{
          component:
            plateRecordDetailModalProps.plateMapType === PlateMapType.RECORD
              ? 'PlateRecordSummary'
              : 'PlateDesignSummary',
          key: 'viewDetailsButton',
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    width: 200,
  },
});
